import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  AppBar, 
  Box, 
  Toolbar, 
  Typography, 
  Button, 
  Container,
  useMediaQuery,
  useTheme,
  IconButton,
  Drawer,
  Paper,
  Stack,
  Grid,
  Collapse
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import EmailIcon from '@mui/icons-material/Email';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ReactMarkdown from 'react-markdown';

// Local imports
import { riverhackColors } from '../theme/riverhackTheme';
import { Event } from '../types/event';
import { generateIcsContent } from '../utils/ics';
import ScrollToTop from '../components/ScrollToTop';

// Assets
import riverhacksLogoWhiteStacked from '../assets/img/Riverhacks_Logo_RGB_White_Stacked (1).png';
import nasaSpaceAppsLogo from '../assets/img/NasaSpaceAppsLogo.png';
import nasaInspiration from '../assets/img/nasaInspiration.png';
import girlstartLogo from '../assets/img/girlstart_sponsor.jpeg';
import impactForwardClubLogo from '../assets/img/impactForwardClub_sponsor.jpeg';
import swiftLogo from '../assets/img/swift_sponsor.png';
import loopMessageLogo from '../assets/img/LoopMessagelogo.svg';
import acmAccLogo from '../assets/img/acm-acc-logo-no-bg.0579f7bc5798e028c7d43dd4bcf0d26c.svg';
import makervilleLogo from '../assets/img/Makerville_D1.png';
import serpApiLogo from '../assets/logos/serpapi.png';
import thunderbirdLogo from '../assets/img/Thunderbird_2023_icon.png';
import cgcsLogo from '../assets/img/cgcs_logo-CMYK_Inverse narrow vert (1).png';
import magikidLogo from '../assets/img/magikid_robotics_logo.png';
import fibonacciFoodLogo from '../assets/img/FF-logo-primary (1).png';
import bespokeFoodLogo from '../assets/img/bespokeFood.png';

// Sections to navigate to
const sections = [
  { id: 'home', label: 'Home' },
  { id: 'main-theme', label: 'Inspiration Paths' },
  { id: 'nasa-challenges', label: 'NASA Challenges' },
  { id: 'announcements', label: 'Announcements' },
  { id: 'schedule', label: 'Schedule' },
  { id: 'faq', label: 'FAQ' },
  { id: 'webai', label: 'Sponsorship' },
  { id: 'contact', label: 'Contact' }
];

// RiverHacks dedicated navbar component
const RiverHacksNavbar = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const navigate = useNavigate();

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleHomeClick = () => {
    navigate('/');
  };

  const scrollToSection = (sectionId: string) => {
    if (sectionId === 'home') {
      handleHomeClick();
      return;
    }
    const element = document.getElementById(sectionId);
    if (element) {
      const navbarHeight = 80; // Height of the navbar
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - navbarHeight;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
    if (isMobile) {
      setDrawerOpen(false);
    }
  };

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          bgcolor: riverhackColors.spaceBlack,
          borderBottom: `1px solid ${riverhackColors.cosmicWhite}20`,
          position: 'sticky',
          top: 0,
          zIndex: theme => theme.zIndex.drawer + 1,
        }}
      >
        <Container maxWidth="xl">
          <Toolbar
            disableGutters
            sx={{
              py: { xs: 2, md: 3 },
              px: { xs: 2, sm: 3, md: 4 },
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              minHeight: { xs: 64, md: 80 },
            }}
          >
            {/* Logo/Title */}
            <Box
              sx={{
                flexGrow: { xs: 1, md: 0 },
                display: 'flex',
                alignItems: 'center',
                minWidth: { xs: 'auto', md: '200px' }
              }}
            >
              <Box
                component="img"
                src={riverhacksLogoWhiteStacked}
                alt="RiverHacks 2025 Logo"
                sx={{
                  height: { xs: '45px', sm: '50px', md: '55px' },
                  width: 'auto'
                }}
              />
            </Box>

            {/* Desktop navigation */}
            <Box sx={{ 
              flexGrow: 1, 
              display: { xs: 'none', lg: 'flex' }, 
              justifyContent: 'center',
              overflow: 'hidden'
            }}>
              {sections.map((section) => (
                <Button
                  key={section.id}
                  onClick={() => scrollToSection(section.id)}
                  sx={{ 
                    my: 2, 
                    mx: 1.5, 
                    color: riverhackColors.cosmicWhite,
                    fontSize: '1.1rem',
                    fontWeight: 'medium',
                    '&:hover': {
                      color: riverhackColors.terracotta,
                    }
                  }}
                >
                  {section.label}
                </Button>
              ))}
            </Box>

            {/* Register button aligned to the right */}
            <Box sx={{ 
              display: { xs: 'none', lg: 'block' },
              minWidth: 'fit-content'
            }}>
              <Button 
                component="a"
                href="https://lu.ma/wudwutav"
                target="_blank"
                rel="noopener noreferrer"
                variant="contained" 
                color="primary"
                className="luma-checkout--button"
                data-luma-action="checkout"
                data-luma-event-id="wudwutav"
                sx={{ 
                  borderRadius: '28px',
                  px: { md: 2, lg: 3 },
                  py: { md: 1, lg: 1.5 },
                  fontSize: { md: '0.9rem', lg: '1rem' },
                  fontWeight: 'bold',
                  textDecoration: 'none',
                  bgcolor: riverhackColors.vibrantOrange,
                  whiteSpace: 'nowrap',
                  '&:hover': {
                    bgcolor: riverhackColors.terracotta,
                  }
                }}
              >
                Register Now
              </Button>
            </Box>

            {/* Mobile menu button */}
            <Box sx={{ 
              display: { xs: 'flex', lg: 'none' }, 
              ml: { xs: 1, sm: 2 },
              minWidth: 'fit-content'
            }}>
              <IconButton
                size="large"
                aria-label="Open navigation menu"
                aria-expanded={drawerOpen}
                onClick={handleDrawerToggle}
                sx={{ 
                  color: riverhackColors.cosmicWhite,
                  p: { xs: 1, sm: 1.5 }
                }}
              >
                <MenuIcon />
              </IconButton>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        open={drawerOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', lg: 'none' },
          '& .MuiDrawer-paper': { 
            width: { xs: '100%', sm: '320px', md: '350px' },
            minWidth: { xs: '100%', sm: '320px' },
            p: { xs: 2, sm: 3 },
            background: `${riverhackColors.spaceBlack}F5`,
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            border: `1px solid ${riverhackColors.cosmicWhite}30`,
            boxShadow: '0 8px 40px rgba(0, 0, 0, 0.8)',
            height: 'auto',
            maxHeight: '100vh',
            top: 0,
            bottom: 'auto',
            borderRadius: { xs: '0', sm: '0 0 0 16px', md: '0 0 0 20px' },
          },
        }}
      >
        <Stack spacing={4} sx={{ alignItems: 'center' }}>
          {/* Drawer Header */}
          <Box sx={{ 
            width: '100%',
            display: 'flex', 
            justifyContent: 'flex-end',
            position: 'relative',
            mb: 2,
          }}>
            {/* Close button - positioned in the top-right corner */}
            <IconButton 
              onClick={handleDrawerToggle}
              aria-label="Close navigation menu"
              sx={{
                color: riverhackColors.cosmicWhite
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Navigation Links */}
          <Stack spacing={2} sx={{ width: '100%', alignItems: 'center' }}>
            {sections.map((section) => (
              <Button
                key={section.id}
                onClick={() => scrollToSection(section.id)}
                fullWidth
                sx={{
                  justifyContent: 'center',
                  color: riverhackColors.cosmicWhite,
                  fontSize: '1.125rem',
                  fontWeight: 600,
                  py: 1.5,
                  borderRadius: 1,
                  '&:hover': {
                    bgcolor: `${riverhackColors.cosmicWhite}10`,
                  },
                  '&:active': {
                    bgcolor: `${riverhackColors.cosmicWhite}20`,
                  },
                }}
              >
                {section.label}
              </Button>
            ))}

            {/* Register Button */}
            <Button 
              component="a"
              href="https://lu.ma/wudwutav"
              target="_blank"
              rel="noopener noreferrer"
              variant="contained" 
              color="primary"
              className="luma-checkout--button"
              data-luma-action="checkout"
              data-luma-event-id="wudwutav"
              fullWidth
              sx={{ 
                mt: 2,
                borderRadius: '28px',
                py: 1.5,
                fontWeight: 'bold',
                textDecoration: 'none',
                bgcolor: riverhackColors.vibrantOrange,
                '&:hover': {
                  bgcolor: riverhackColors.terracotta,
                }
              }}
            >
              Register Now
            </Button>
          </Stack>
        </Stack>
      </Drawer>
    </>
  );
};

// Section component for creating each main section
const Section = ({ 
  id, 
  title, 
  children, 
  fullWidth = false
}: { 
  id: string, 
  title: string, 
  children: React.ReactNode,
  fullWidth?: boolean 
}) => (
  <Box 
    id={id} 
    component="section"
    sx={{ 
      py: fullWidth ? 0 : { xs: 6, sm: 8, md: 10 },
      minHeight: fullWidth ? '100vh' : 'auto',
      bgcolor: riverhackColors.spaceBlack,
      '&:nth-of-type(even)': {
        bgcolor: riverhackColors.starField
      }
    }}
  >
    {fullWidth ? children : (
      <Container maxWidth="lg">
        <Typography 
          variant="h3" 
          component="h2" 
          align="center" 
          gutterBottom 
          sx={{ 
            fontWeight: 'bold',
            mb: { xs: 4, sm: 5, md: 6 },
            color: riverhackColors.cosmicWhite,
            fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
            px: { xs: 2, sm: 0 }
          }}
        >
          {title}
        </Typography>
        <Box sx={{ px: { xs: 2, sm: 3, md: 0 } }}>
          {children}
        </Box>
      </Container>
    )}
  </Box>
);

// Add this type for the Luma global object
declare global {
  interface Window {
    LumaCheckout?: any;
  }
}

// Add this type definition
interface Announcement {
  id: string;
  body: string;
  published: Date;
}

const announcements: Announcement[] = [
  {
    id: '1',
    body: '**August 21: Official NASA Challenges Released!** The complete list of NASA Space Apps Challenge 2025 categories is now available. Check out the full catalog of challenges at [spaceappschallenge.org](https://www.spaceappschallenge.org/2025/challenges/)',
    published: new Date('2025-08-21T10:00:00'),
  },
  {
    id: '2',
    body: '**September 20: Official Sponsors List Coming Soon!** The next big communication will be our official sponsors list release. Stay tuned to see which amazing organizations are supporting RiverHacks 2025!',
    published: new Date('2025-09-20T14:00:00'),
  }
];

// RiverHacks NASA Space Apps Challenge Schedule  
const hackathonSchedule: Event[] = [
  // Saturday, October 4, 2025
  {
    id: 'sat-1',
    name: 'Signup, Registration & Team Formation + Breakfast',
    description: 'The RiverHacks<> NASA Space Apps Challenge kicks off! Registration, networking, and team formation begins. Breakfast served for participants only. Paper bracelets will be distributed for grouping. Master of Ceremony encourages participants to mingle, share skillsets and form teams on their own. If your team is not fully formed with 6 people, the next section will benefit you.',
    location: 'Main Event Space',
    start: new Date('2025-10-04T09:00:00'),
    end: new Date('2025-10-04T10:15:00'),
    type: 'Main Events'
  },
  {
    id: 'sat-2',
    name: 'Team Formation Activity + Team Registration',
    description: 'Master of Ceremony facilitates a gamified way to team up people who didn\'t have the opportunity to form a full team of 6 people. During this activity, 2 volunteers will be assigned to start registering participants teams with full names, emails, team leader name and team name.',
    location: 'Main Event Space',
    start: new Date('2025-10-04T10:15:00'),
    end: new Date('2025-10-04T10:30:00'),
    type: 'Main Events'
  },
  {
    id: 'sat-3',
    name: 'Hackathon Remarks',
    description: 'With the teams already formed and ready to register, we invite to the front all the people that make this hackathon possible. ACC representatives and NASA Space Apps lead will walk you through the details of this amazing event.',
    location: 'Main Event Space',
    start: new Date('2025-10-04T10:30:00'),
    end: new Date('2025-10-04T11:00:00'),
    type: 'Main Events'
  },
  {
    id: 'sat-4',
    name: 'Hackathon Officially Starts!',
    description: 'The hackathon officially begins! Teams can start working on their chosen challenge(s) or creating their own ideas. Take the time, if you haven\'t, to nominate a team lead and provide all the details of your team in the information desk. Team leads are responsible for balancing work, gaining team interaction, and if the team has minors...to remotely connect the young participants who desire to continue from home. Throughout the event, the slack channel will be available for communication and support from mentors, organizers, and other participants. Make sure to connect and collaborate as you start building solutions.',
    location: 'Hacking Spaces',
    start: new Date('2025-10-04T11:00:00'),
    end: new Date('2025-10-04T13:00:00'),
    type: 'Hacking'
  },
  {
    id: 'sat-5',
    name: 'Lunch',
    description: 'Take a well-deserved break to enjoy lunch and recharge. During this time, participants are encouraged to network, share ideas, and discuss their progress with other teams. It\'s a perfect opportunity to refuel and engage in informal conversations that could inspire new solutions.',
    location: 'Dining Area',
    start: new Date('2025-10-04T13:00:00'),
    end: new Date('2025-10-04T14:00:00'),
    type: 'Food'
  },
  {
    id: 'sat-6',
    name: 'Hackathon Resumes + 1:1 Mentorship Opportunities (Optional)',
    description: 'Take the advantage of listening to a mentor early on with this private team 1:1 conversation in the rooms 3346 and 3347. To take advantage of this opportunity of fast paced 7 minutes feedback, the team lead makes a reservation by putting his name on the door sign sheet. Rooms available: Idea formation Mentors (EDGE Team - 3346) and AI Mentor (Balaji Varadarajan -3347) from 2-3pm.',
    location: 'Hacking Spaces / Rooms 3346 & 3347',
    start: new Date('2025-10-04T14:00:00'),
    end: new Date('2025-10-04T15:00:00'),
    type: 'Hacking'
  },
  {
    id: 'sat-7',
    name: 'In-Person Workshop 1: Use of Large Language Models for Agents Orchestration (Optional)',
    description: 'This Beginner Friendly workshop will focus on the implementation of AI tools like CLI Agents.',
    location: 'Workshop Room',
    start: new Date('2025-10-04T15:00:00'),
    end: new Date('2025-10-04T16:00:00'),
    type: 'Workshops'
  },
  {
    id: 'sat-8',
    name: 'In-Person Workshop 2: Design Systems for Aerospace Software - Cadence Systems (Optional)',
    description: 'Attend this industry expert talk to understand the basis of software applied to the space industry.',
    location: 'Workshop Room',
    start: new Date('2025-10-04T16:00:00'),
    end: new Date('2025-10-04T17:00:00'),
    type: 'Workshops'
  },
  {
    id: 'sat-9',
    name: 'Dismissal for under 18 participants elementary/high schoolers for Day 2 + Snack Break',
    description: 'Wrap up the first day of hacking for young participants. They\'re encouraged to continue hacking at home and continue working on your projects remotely. Teams that are fully <18 participants will summarize their progress, discuss next steps, and make a plan for Day 2.',
    location: 'Main Event Space',
    start: new Date('2025-10-04T17:00:00'),
    end: new Date('2025-10-04T19:00:00'),
    type: 'Main Events'
  },
  {
    id: 'sat-10',
    name: 'Dinner Break (Kababji Grill) + Virtual Workshop 3: Advanced use of Large Language Models for Agents Orchestration',
    description: 'Participants will take a deserved break if they need to. During this dinner break they will enjoy the Halal Food from Kababji Grill Restaurant: Gyro Wraps, Chicken Shawarma Wraps and Falafel Wraps for vegetarians. At the same time if they desire to attend the virtual workshop session, that will be on the floor downstairs in room 3210.',
    location: 'Dining Area / Room 3210',
    start: new Date('2025-10-04T19:00:00'),
    end: new Date('2025-10-04T20:00:00'),
    type: 'Food'
  },
  {
    id: 'sat-11',
    name: 'Hacking Continues All Night',
    description: 'Overnight hacking session for dedicated teams. Continue development, refine solutions, and make progress toward final submissions.',
    location: 'Hacking Spaces',
    start: new Date('2025-10-04T20:00:00'),
    end: new Date('2025-10-05T07:00:00'),
    type: 'Hacking'
  },

  // Sunday, October 5, 2025
  {
    id: 'sun-1',
    name: 'Breakfast is served (Only for participants)',
    description: 'Participants get energy for the final straw of the hackathon.',
    location: 'Dining Area',
    start: new Date('2025-10-05T07:00:00'),
    end: new Date('2025-10-05T09:00:00'),
    type: 'Food'
  },
  {
    id: 'sun-2',
    name: 'Check-in for Returning Participants - Day 2 of Hackathon',
    description: 'Return to your team and see. This is a time to organize tasks, set goals, and prepare for a full day of development as you approach the final stretch of the hackathon.',
    location: 'Check-in Area',
    start: new Date('2025-10-05T09:00:00'),
    end: new Date('2025-10-05T09:30:00'),
    type: 'Main Events'
  },
  {
    id: 'sun-3',
    name: 'Quick Snack for Returning Participants',
    description: 'Use this snack time to energize, discuss your team\'s strategy, and set the pace for completing your project. Afterward, dive straight back into development, as this will be a critical time for refining your solution.',
    location: 'Snack Area',
    start: new Date('2025-10-05T09:30:00'),
    end: new Date('2025-10-05T11:00:00'),
    type: 'Food'
  },
  {
    id: 'sun-4',
    name: 'Mentoring for 5\' Pitch (Suggested but Optional)',
    description: 'As a team, decide how you are going to design slides, who will present (1 or 2 people) and practice for a winner pitch. Teams are encouraged to come to the "Mentor\'s Lounge", ask for a 1:1 session with mentors and polish the 5 minute pitch.',
    location: 'Mentor\'s Lounge',
    start: new Date('2025-10-05T11:00:00'),
    end: new Date('2025-10-05T12:00:00'),
    type: 'Mentorship'
  },
  {
    id: 'sun-5',
    name: 'Prepare your GitHub Submission',
    description: 'If not, this could be your last opportunity to get feedback and guidance from mentors before submitting your project. Make sure all aspects of your project are polished and ready for presentation. Address any final issues and implement last-minute adjustments.',
    location: 'Hacking Spaces',
    start: new Date('2025-10-05T12:00:00'),
    end: new Date('2025-10-05T13:00:00'),
    type: 'Hacking'
  },
  {
    id: 'sun-6',
    name: 'Submissions Deadline + Lunch is Served + Judges Check-in (Conference Room Mentors / Judges Lounge)',
    description: 'Eat some pizza from Joe\'s Slice of Sicily, relax and celebrate the effort poured in this hackathon with your team while you prepare yourself to pitch your idea to everyone in the audience.',
    location: 'Dining Area / Conference Room Lounge',
    start: new Date('2025-10-05T13:00:00'),
    end: new Date('2025-10-05T13:30:00'),
    type: 'Main Events'
  },
  {
    id: 'sun-7',
    name: 'Pitch ideas to Judges and Everyone',
    description: 'Get ready to pitch on stage! Each team will have 5 minutes per team to showcase your solution, explain their approach and demonstrate knowledge with chosen challenges. Judges will be able to ask 2 questions and evaluate projects based on RiverHacks and NASA Space Apps criteria.',
    location: 'Main Presentation Stage',
    start: new Date('2025-10-05T13:30:00'),
    end: new Date('2025-10-05T16:00:00'),
    type: 'Presentations'
  },
  {
    id: 'sun-8',
    name: 'Winners Announcement and Closing Ceremony',
    description: 'Local judges will announce the winning teams based on their evaluations. Winners will receive recognition and prizes, and all participants will be celebrated for their hard work and collaboration. Closing remarks will emphasize the importance of continuing innovation and working towards making a positive impact through STEM.',
    location: 'Main Hall',
    start: new Date('2025-10-05T16:30:00'),
    end: new Date('2025-10-05T17:00:00'),
    type: 'Ceremonies'
  },
  {
    id: 'sun-9',
    name: 'End of the Event',
    description: 'Official conclusion of RiverHacks 2025. Thank you for an amazing hackathon experience!',
    location: 'Main Hall',
    start: new Date('2025-10-05T17:00:00'),
    end: new Date('2025-10-05T17:00:00'),
    type: 'Ceremonies'
  }
];

// Update the ScheduleSection component to use the new name
const ScheduleSection = () => {
  const [selectedFilter, setSelectedFilter] = useState<string | null>(null);

  // Update to use hackathonSchedule
  const filters = Array.from(new Set(hackathonSchedule.map(event => event.type)));

  const groupedEvents = hackathonSchedule.reduce((acc, event) => {
    // Format the date as "Day, Month Date"
    const day = event.start.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
    if (!acc[day]) {
      acc[day] = [];
    }
    acc[day].push(event);
    return acc;
  }, {} as Record<string, Event[]>);

  return (
    <Box sx={{ width: '100%' }}>
      {/* Friday Virtual Event Notice */}
      <Box 
        sx={{ 
          mb: 4, 
          p: 3,
          bgcolor: riverhackColors.starField,
          border: `1px solid ${riverhackColors.nasaBlue}30`,
          borderRadius: 2,
          maxWidth: '1000px',
          mx: 'auto'
        }}
      >
        <Typography 
          variant="h6" 
          sx={{ 
            color: riverhackColors.nasaBlue,
            fontWeight: 'bold',
            mb: 1
          }}
        >
          **Friday, October 3, 2025 - Virtual Optional Remarks from the Global Space Apps Team**
        </Typography>
        <Typography 
          variant="body1" 
          sx={{ 
            color: riverhackColors.cosmicWhite,
            fontSize: '1rem',
            lineHeight: 1.6
          }}
        >
          16:00 - Welcome to NASA Space Apps kick off video featuring messages from special NASA guests. Global Collaborator Offers released. This is a virtual and optional session.
        </Typography>
      </Box>

      {/* Filters */}
      <Paper 
        elevation={0}
        sx={{ 
          p: 2, 
          mb: 4,
          bgcolor: 'background.paper',
          border: `1px solid ${riverhackColors.deepPurple}20`,
          maxWidth: '1000px',  // Add max width
          mx: 'auto'  // Center the filters
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
          <Stack direction="row" spacing={1} flexWrap="wrap">
            {filters.map((filter) => (
              <Button
                key={filter}
                variant={selectedFilter === filter ? "contained" : "outlined"}
                onClick={() => setSelectedFilter(selectedFilter === filter ? null : filter)}
                size="small"
                sx={{
                  borderRadius: '16px',
                  textTransform: 'none'
                }}
              >
                {filter}
              </Button>
            ))}
          </Stack>
          <Button
            startIcon={<CalendarMonthIcon />}
            variant="outlined"
            onClick={() => {
              const icsContent = generateIcsContent(hackathonSchedule);
              const blob = new Blob([icsContent], { type: 'text/calendar' });
              const url = window.URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              link.setAttribute('download', 'riverhacks-schedule.ics');
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            }}
            sx={{
              borderRadius: '16px',
            }}
          >
            Add to Calendar
          </Button>
        </Stack>
      </Paper>

      {/* Schedule Grid */}
      <Box 
        sx={{ 
          maxWidth: '1200px',
          mx: 'auto'
        }}
      >
        <Grid container spacing={6}>
          {Object.entries(groupedEvents).map(([day, events]) => (
            <Grid item xs={12} md={6} key={day}>
              <Typography
                variant="h5"
                sx={{
                  mb: 2,
                  color: riverhackColors.cosmicWhite,
                  fontWeight: 'bold'
                }}
              >
                {day}
              </Typography>
              <Stack spacing={2}>
                {events
                  .filter(event => !selectedFilter || event.type === selectedFilter)
                  .map((event) => (
                    <Paper
                      key={event.id}
                      elevation={0}
                      sx={{
                        p: 2,
                        bgcolor: riverhackColors.starField,
                        border: `1px solid ${riverhackColors.cosmicWhite}20`,
                        borderLeft: `4px solid ${riverhackColors.terracotta}`
                      }}
                    >
                      <Stack spacing={1}>
                        <Typography 
                          variant="h6" 
                          sx={{ 
                            color: riverhackColors.cosmicWhite,
                            fontWeight: 'bold'
                          }}
                        >
                          {event.name}
                        </Typography>
                        <Typography 
                          variant="body2" 
                          sx={{
                            color: riverhackColors.terracotta,
                            fontWeight: '500'
                          }}
                        >
                          {event.start.toLocaleTimeString([], { 
                            hour: '2-digit', 
                            minute: '2-digit'
                          })} - {event.end.toLocaleTimeString([], { 
                            hour: '2-digit', 
                            minute: '2-digit'
                          })}
                        </Typography>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: '#0066CC',
                            fontWeight: '500'
                          }}
                        >
                          📍 {event.location}
                        </Typography>
                        <Typography 
                          variant="body2"
                          sx={{
                            color: riverhackColors.cosmicWhite,
                            lineHeight: 1.5
                          }}
                        >
                          {event.description}
                        </Typography>
                      </Stack>
                    </Paper>
                  ))}
              </Stack>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
};

// Add this interface before the faqList definition
interface FAQ {
  isActive: boolean;
  question: string;
  answer: string;
}

// Update the faqList type annotation
const faqList: FAQ[] = [
  {
    isActive: false,
    question: "Rules and Code of Conduct",
    answer: "All work must be done at the event—no pre-built projects allowed. If you didn't build it here, you can't demo it. All attendees (hackers, supporters, mentors, volunteers, etc.) must follow our code of conduct. Play fair, be respectful, and keep it fun."
  },
  {
    isActive: false,
    question: "How will teams work?",
    answer: "Teams will be formed organically with up to 6 members, including at least 2 high school students (13+ years old). If you already have a team of 4 people, you're all set and will be paired with 2 high school students. We'll make exceptions for teams of 7, but that won't be the norm. No team? No problem! Meeting new people and collaborating is half the fun. We'll use a colored bracelet system to help facilitate team formation naturally throughout the event."
  },
  {
    isActive: false,
    question: "What should I bring?",
    answer: "A valid university ID or proof that you're over 18, a laptop, chargers, and whatever else you need to survive 30+ hours of coding. Weapons, alcohol, illegal substances, and power tools? Hard no. But bring your best ideas, good vibes, and an unlimited supply of high-fives."
  },
  {
    isActive: false,
    question: "Travel Reimbursements",
    answer: "Unfortunately, we can't cover travel costs this year, but we highly recommend budget-friendly options like Uber, Lyft, CapMetro, or, if you're feeling adventurous, Lime/Bird scooters. Consider it part of the hackathon challenge—how to get here efficiently!"
  },
  {
    isActive: false,
    question: "Will there be prizes?",
    answer: "Absolutely! We'll be giving away prizes for the top projects, mini-challenges, and maybe even some surprise categories. Full details will be revealed at the Opening Ceremony, so bring your A-game."
  },
  {
    isActive: false,
    question: "How much will this cost me?",
    answer: "Nothing. Zero. Zilch. It's completely free. We'll cover your Wi-Fi, meals, workspace, swag, and, of course, eternal bragging rights."
  },
  {
    isActive: false,
    question: "Where will RiverHacks take place?",
    answer: "RiverHacks will be hosted at the Center for Government and Civic Service (CGCS).\nAddress: 1218 West Ave, Austin, TX 78701\nEverything—hacking, judging, and mini-events—will happen in person, so get ready for an action-packed weekend."
  },
  {
    isActive: false,
    question: "When will RiverHacks take place?",
    answer: "Check-in starts Saturday, October 4 at 9:00 AM, and the hackathon officially ends Sunday, October 5 at 5:30 PM. That's 30+ hours of non-stop creativity, coding, and possibly too much caffeine."
  },
  {
    isActive: false,
    question: "Will parking be provided?",
    answer: "Yes! Free parking is available at CGCS.\nParking Garage: 816 W 12th St, Austin, TX 78701\nHeads up—the garage locks up at 10:00 PM on Saturday, so plan accordingly. It reopens at 7:00 AM on Sunday, so don't get stuck unless you're planning to code through the night (which is totally encouraged)."
  },
  {
    isActive: false,
    question: "What if I missed the application deadline?",
    answer: "No worries! We'll have walk-in registration on Saturday, October 4 at 9:00 AM. Just show up, sign in, and get hacking."
  },
  {
    isActive: false,
    question: "What are the NASA Space Apps Challenge categories?",
    answer: "We'll be working with the official NASA Space Apps Challenge 2025 categories, including challenges like 'Animation Celebration of Terra Data!', 'Meteor Madness', 'Embiggen Your Eyes!', and more. Check out the full list at spaceappschallenge.org/2025/challenges/"
  },
  {
    isActive: false,
    question: "I have more questions. Who do I ask?",
    answer: "We've got answers! Shoot us an email at stefano.casafrancalaos@austincc.edu, and we'll be happy to help. Or, you know, just show up and ask us in person—we're friendly."
  }
];

// Update the FaqItem component props type
const FaqItem = ({ faq }: { faq: FAQ }) => {
  const [isOpen, setIsOpen] = useState(faq.isActive);

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 3,
        overflow: 'hidden',
        bgcolor: riverhackColors.starField,
        border: `1px solid ${riverhackColors.cosmicWhite}30`,
        borderRadius: 2,
        transition: 'all 0.3s ease',
        '&:hover': {
          borderColor: riverhackColors.terracotta,
        }
      }}
    >
      <Button
        onClick={() => setIsOpen(!isOpen)}
        fullWidth
        sx={{
          justifyContent: 'space-between',
          p: 3,
          color: riverhackColors.cosmicWhite,
          textTransform: 'none',
          fontSize: '1.1rem',
          fontWeight: 'medium',
          '&:hover': {
            backgroundColor: `${riverhackColors.cosmicWhite}10`
          }
        }}
      >
        <Typography 
          sx={{ 
            textAlign: 'left',
            fontWeight: isOpen ? 'bold' : 'medium',
            color: isOpen ? riverhackColors.terracotta : riverhackColors.cosmicWhite
          }}
        >
          {faq.question}
        </Typography>
        {isOpen ? <RemoveIcon /> : <AddIcon />}
      </Button>
      <Collapse in={isOpen}>
        <Box
          sx={{
            p: 3,
            bgcolor: riverhackColors.spaceBlack,
            borderTop: `1px solid ${riverhackColors.cosmicWhite}20`
          }}
        >
          <Typography
            variant="body1"
            dangerouslySetInnerHTML={{ __html: faq.answer }}
            sx={{
              color: riverhackColors.cosmicWhite,
              lineHeight: 1.6,
              whiteSpace: 'pre-line',
              fontSize: '1rem',
              fontWeight: '400',
              '& a': {
                color: riverhackColors.terracotta,
                textDecoration: 'none',
                fontWeight: 'medium',
                '&:hover': {
                  textDecoration: 'underline'
                }
              }
            }}
          />
        </Box>
      </Collapse>
    </Paper>
  );
};

// Update the FAQ section component
const FaqSection = () => {
  return (
    <Box sx={{ width: '100%', maxWidth: '1200px', mx: 'auto' }}>
      <Typography
        variant="body1"
        align="center"
        sx={{
          mb: 6,
          maxWidth: '800px',
          mx: 'auto',
          color: riverhackColors.cosmicWhite,
          fontSize: '1.2rem',
          lineHeight: 1.6,
          fontWeight: '400'
        }}
      >
        Got questions? We've got answers! Check out our frequently asked questions below.
      </Typography>

      <Grid container spacing={6}>
        <Grid xs={12} md={6}>
          {faqList.slice(0, Math.ceil(faqList.length / 2)).map((faq, i) => (
            <FaqItem key={i} faq={faq} />
          ))}
        </Grid>
        <Grid xs={12} md={6}>
          {faqList.slice(Math.ceil(faqList.length / 2)).map((faq, i) => (
            <FaqItem key={i} faq={faq} />
          ))}
        </Grid>
      </Grid>
    </Box>
  );
};

// Add these interfaces near the top of the file with other interfaces
interface Sponsor {
  name: string;
  logo: string | null;
  website: string;
}

interface SponsorSection {
  title: string;
  sponsors: Sponsor[];
  benefits?: string[];
}

// Sponsor data structure defined but titleSponsor is handled directly in the component

const sponsorSections: SponsorSection[] = [
  {
    title: "🥈 Silver Sponsor",
    benefits: [],
    sponsors: [
      {
        name: "Thunderbird",
        logo: thunderbirdLogo,
        website: "#", // Add actual website when available
      }
    ]
  },
  {
    title: "🔸 StartUp Supporter",
    benefits: [
      "Social media and website recognition"
    ],
    sponsors: [
      {
        name: "Impact Forward Club",
        logo: impactForwardClubLogo,
        website: "#", // Add actual website when available
      },
      {
        name: "MagiKid Robotics",
        logo: magikidLogo,
        website: "#", // Add actual website when available
      },
      {
        name: "Wyatt Consulting",
        logo: null, // Text-only sponsor
        website: "#", // Add actual website when available
      },
      {
        name: "MakerVille",
        logo: makervilleLogo,
        website: "#", // Add actual website when available
      }
    ]
  }
];

// Update the SponsorLogo component to handle sponsors with and without logos
const SponsorLogo = ({ sponsor, className, showName = false }: { sponsor: Sponsor; className?: any; showName?: boolean }) => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        p: 4,
        bgcolor: 'background.paper',
        borderRadius: 2,
        border: `1px solid ${riverhackColors.deepPurple}20`,
        ...(className || {})
      }}
    >
      {sponsor.logo ? (
        <>
          <Box
            component="img"
            src={sponsor.logo}
            alt={`${sponsor.name} logo`}
            sx={{
              maxWidth: '100%',
              height: 'auto',
              objectFit: 'contain',
              width: '200px',
              margin: '0 auto', // Center the image
              display: 'block' // Ensure proper margin centering
            }}
          />
          {showName && (
            <Typography
              variant="h6"
              sx={{
                color: riverhackColors.deepPurple,
                fontWeight: 'bold',
                textAlign: 'center',
                fontSize: '1.2rem',
                mt: 2
              }}
            >
              {sponsor.name}
            </Typography>
          )}
        </>
      ) : (
        <Typography
          variant="h6"
          sx={{
            color: riverhackColors.deepPurple,
            fontWeight: 'bold',
            textAlign: 'center',
            fontSize: '1.2rem'
          }}
        >
          {sponsor.name}
        </Typography>
      )}
    </Box>
  );
};

// Update the SponsorsSection component to handle the new sponsor structure
const SponsorsSection = () => {
  return (
    <Box sx={{ maxWidth: '1200px', mx: 'auto' }}>
      {/* SerpAPI Title Sponsor - Large Hero Section */}
      <Box sx={{ mb: 8 }}>
        <Paper
          elevation={3}
          sx={{
            position: 'relative',
            background: `linear-gradient(135deg, ${riverhackColors.starField} 0%, ${riverhackColors.deepPurple} 100%)`,
            border: `2px solid ${riverhackColors.terracotta}`,
            borderRadius: 4,
            overflow: 'hidden',
            minHeight: '400px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            p: { xs: 4, md: 6 },
            textAlign: 'center'
          }}
        >
          {/* Decorative Background Pattern */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              opacity: 0.1,
              backgroundImage: `radial-gradient(circle at 20% 20%, ${riverhackColors.cosmicWhite} 2px, transparent 2px),
                                radial-gradient(circle at 80% 80%, ${riverhackColors.cosmicWhite} 2px, transparent 2px),
                                radial-gradient(circle at 40% 70%, ${riverhackColors.cosmicWhite} 1px, transparent 1px)`,
              backgroundSize: '100px 100px, 80px 80px, 60px 60px',
            }}
          />
          
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Typography
              variant="h3"
              sx={{
                color: riverhackColors.terracotta,
                fontWeight: 'bold',
                mb: 2,
                fontSize: { xs: '2rem', md: '3rem' },
                textShadow: `2px 2px 4px ${riverhackColors.starField}80`
              }}
            >
              🏆 TITLE SPONSOR
            </Typography>
            
            <Typography
              variant="h4"
              sx={{
                color: riverhackColors.cosmicWhite,
                fontWeight: 'bold',
                mb: 4,
                fontSize: { xs: '1.5rem', md: '2rem' }
              }}
            >
              RiverHacks &lt;&gt; NASA Space Apps
              <br />
              <Box
                component="span"
                sx={{
                  color: riverhackColors.nasaBlue,
                  fontSize: { xs: '1.2rem', md: '1.5rem' }
                }}
              >
                Powered by SerpAPI
              </Box>
            </Typography>

            {/* SerpAPI Logo - Large and Prominent */}
            <Box sx={{ mb: 4 }}>
              <Box
                component="img"
                src={serpApiLogo}
                alt="SerpAPI - Title Sponsor"
                sx={{
                  maxWidth: { xs: '250px', md: '400px' },
                  height: 'auto',
                  objectFit: 'contain',
                  filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.3))'
                }}
              />
            </Box>
            
            <Typography
              variant="h6"
              sx={{
                color: riverhackColors.cosmicWhite,
                fontSize: { xs: '1rem', md: '1.2rem' },
                fontStyle: 'italic',
                opacity: 0.9,
                mb: 3,
                maxWidth: '600px',
                mx: 'auto'
              }}
            >
              Thank you SerpAPI for empowering innovation at
              <br />
              Austin's premier hackathon!
            </Typography>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                gap: 2,
                flexWrap: 'wrap'
              }}
            >
              <Box
                sx={{
                  bgcolor: riverhackColors.terracotta + '20',
                  px: 3,
                  py: 1,
                  borderRadius: 2,
                  border: `1px solid ${riverhackColors.terracotta}`
                }}
              >
                <Typography variant="body2" sx={{ color: riverhackColors.cosmicWhite, fontWeight: 'bold' }}>
                  🚀 Custom Challenge Track
                </Typography>
              </Box>
              <Box
                sx={{
                  bgcolor: riverhackColors.terracotta + '20',
                  px: 3,
                  py: 1,
                  borderRadius: 2,
                  border: `1px solid ${riverhackColors.terracotta}`
                }}
              >
                <Typography variant="body2" sx={{ color: riverhackColors.cosmicWhite, fontWeight: 'bold' }}>
                  🎯 Exclusive Naming Rights
                </Typography>
              </Box>
              <Box
                sx={{
                  bgcolor: riverhackColors.terracotta + '20',
                  px: 3,
                  py: 1,
                  borderRadius: 2,
                  border: `1px solid ${riverhackColors.terracotta}`
                }}
              >
                <Typography variant="body2" sx={{ color: riverhackColors.cosmicWhite, fontWeight: 'bold' }}>
                  💡 Innovation Partnership
                </Typography>
              </Box>
            </Box>
          </Box>
        </Paper>
      </Box>

      {/* Sponsor Sections */}
      {sponsorSections.map((section, index) => (
        <Box
          key={section.title}
          sx={{
            mb: 8,
            pb: index !== sponsorSections.length - 1 ? 8 : 0,
            borderBottom: index !== sponsorSections.length - 1 ? `1px solid ${riverhackColors.deepPurple}20` : 'none'
          }}
        >
          <Typography
            variant="h4"
            align="center"
            sx={{
              color: riverhackColors.cosmicWhite,
              mb: 4,
              fontWeight: 'bold'
            }}
          >
            {section.title}
          </Typography>
          
          {/* Benefits List */}
          {section.benefits && section.benefits.length > 0 && (
            <Box sx={{ mb: 4, maxWidth: '800px', mx: 'auto' }}>
              <Stack spacing={1}>
                {section.benefits.map((benefit, benefitIndex) => (
                  <Typography
                    key={benefitIndex}
                    variant="body1"
                    sx={{
                      color: riverhackColors.cosmicWhite,
                      fontSize: '1rem',
                      lineHeight: 1.6,
                      textAlign: 'center',
                      '&::before': {
                        content: '"• "',
                        color: riverhackColors.terracotta,
                        fontWeight: 'bold'
                      }
                    }}
                  >
                    {benefit}
                  </Typography>
                ))}
              </Stack>
            </Box>
          )}

          {section.sponsors.length > 0 ? (
            section.title.includes("Gold Sponsor") || section.title.includes("Silver Sponsor") ? (
              // Special Gold/Silver Sponsor Layout
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 4
                }}
              >
                {section.sponsors.map((sponsor) => (
                  <Box
                    key={sponsor.name}
                    sx={{
                      background: section.title.includes("Gold Sponsor") 
                        ? `linear-gradient(135deg, ${riverhackColors.terracotta}20 0%, ${riverhackColors.sandBeige}15 100%)`
                        : `linear-gradient(135deg, ${riverhackColors.sandBeige}15 0%, ${riverhackColors.terracotta}10 100%)`,
                      borderRadius: 4,
                      p: section.title.includes("Gold Sponsor") ? 8 : 6,
                      border: section.title.includes("Gold Sponsor") 
                        ? `3px solid ${riverhackColors.terracotta}40`
                        : `2px solid ${riverhackColors.sandBeige}30`,
                      boxShadow: section.title.includes("Gold Sponsor") 
                        ? `0 12px 40px ${riverhackColors.deepPurple}30`
                        : `0 8px 32px ${riverhackColors.deepPurple}20`,
                      maxWidth: section.title.includes("Gold Sponsor") ? '600px' : '500px',
                      width: '100%'
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: section.title.includes("Gold Sponsor") ? 4 : 3
                      }}
                    >
                      {sponsor.logo && (
                        <Box
                          component="img"
                          src={sponsor.logo}
                          alt={`${sponsor.name} logo`}
                          sx={{
                            width: section.title.includes("Gold Sponsor") ? '576px' : '360px',
                            height: section.title.includes("Gold Sponsor") ? '288px' : '180px',
                            objectFit: 'contain',
                            filter: 'brightness(1.1)'
                          }}
                        />
                      )}
                      {(section.title.includes("Silver Sponsor") || !sponsor.logo) && (
                        <Typography
                          variant="h5"
                          sx={{
                            color: riverhackColors.cosmicWhite,
                            fontWeight: 'bold',
                            textAlign: 'center'
                          }}
                        >
                          {sponsor.name}
                        </Typography>
                      )}
                      <Typography
                        variant="body1"
                        sx={{
                          color: riverhackColors.cosmicWhite,
                          textAlign: 'center',
                          opacity: 0.9,
                          fontStyle: 'italic',
                          fontSize: section.title.includes("Gold Sponsor") ? '1.1rem' : '1rem'
                        }}
                      >
                        {section.title.includes("Gold Sponsor") 
                          ? "Thank you CGCS for being our venue partner and supporting student innovation"
                          : "Thank you Thunderbird for supporting the innovation of the next-gen of leaders"
                        }
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : section.title.includes("StartUp Supporter") ? (
              // Special balanced layout for StartUp Supporter (4 logos: smaller-larger-larger-smaller)
              <Grid container spacing={3} alignItems="center" justifyContent="center">
                {section.sponsors.map((sponsor, index) => {
                  const isMiddle = index === 1 || index === 2;
                  return (
                    <Grid 
                      item 
                      xs={12} 
                      sm={6}
                      md={isMiddle ? 3.5 : 2.25} // Middle containers larger
                      key={sponsor.name}
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center'
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          p: isMiddle ? 5 : 3, // More padding for middle containers
                          bgcolor: 'background.paper',
                          borderRadius: 2,
                          border: `1px solid ${riverhackColors.deepPurple}20`,
                          minHeight: isMiddle ? '200px' : '160px', // Consistent heights
                          width: '100%',
                          boxShadow: isMiddle ? '0 4px 20px rgba(0,0,0,0.1)' : '0 2px 10px rgba(0,0,0,0.05)',
                          transition: 'all 0.3s ease'
                        }}
                      >
                        {sponsor.logo ? (
                          <Box
                            component="img"
                            src={sponsor.logo}
                            alt={`${sponsor.name} logo`}
                            sx={{
                              maxWidth: '100%',
                              height: 'auto',
                              objectFit: 'contain',
                              width: isMiddle ? '180px' : '140px', // Larger logos for middle
                              maxHeight: isMiddle ? '120px' : '100px'
                            }}
                          />
                        ) : (
                          <Typography
                            variant={isMiddle ? "h5" : "h6"}
                            sx={{
                              color: riverhackColors.deepPurple,
                              fontWeight: isMiddle ? '700' : '600',
                              textAlign: 'center',
                              fontSize: isMiddle ? '1.5rem' : '1.2rem',
                              letterSpacing: isMiddle ? '0.5px' : '0.25px',
                              fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
                              background: isMiddle ? `linear-gradient(135deg, ${riverhackColors.deepPurple}, ${riverhackColors.terracotta})` : riverhackColors.deepPurple,
                              backgroundClip: isMiddle ? 'text' : 'unset',
                              WebkitBackgroundClip: isMiddle ? 'text' : 'unset',
                              WebkitTextFillColor: isMiddle ? 'transparent' : 'inherit',
                              textShadow: isMiddle ? 'none' : '0 1px 2px rgba(0,0,0,0.1)'
                            }}
                          >
                            {sponsor.name}
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            ) : (
              // Regular sponsor layout for other tiers
              <Grid 
                container 
                spacing={4} 
                alignItems="center"
                justifyContent="center"
              >
                {section.sponsors.map((sponsor) => (
                  <Grid 
                    item 
                    xs={12} 
                    sm={6}
                    md={4} 
                    key={sponsor.name}
                    sx={{
                      display: 'flex',
                      justifyContent: 'center'
                    }}
                  >
                    <SponsorLogo sponsor={sponsor} showName={false} />
                  </Grid>
                ))}
              </Grid>
            )
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography
                variant="h6"
                sx={{
                  color: riverhackColors.cosmicWhite,
                  fontStyle: 'italic',
                  opacity: 0.7
                }}
              >
                Sponsorship opportunities available
              </Typography>
            </Box>
          )}
        </Box>
      ))}

      {/* Sponsorship CTA */}
      <Box 
        sx={{ 
          mt: 8, 
          textAlign: 'center',
          p: 4,
          bgcolor: `${riverhackColors.sandBeige}30`,
          borderRadius: 2
        }}
      >
        <Typography 
          variant="h5"
          sx={{ 
            mb: 2,
            color: riverhackColors.cosmicWhite,
            fontWeight: 'medium'
          }}
        >
          Interested in becoming a sponsor?
        </Typography>
        <Typography 
          variant="body1"
          sx={{ 
            fontSize: '1.1rem',
            '& a': {
              color: riverhackColors.vibrantOrange,
              textDecoration: 'none',
              fontWeight: 'bold',
              '&:hover': {
                textDecoration: 'underline'
              }
            }
          }}
        >
          Reach out to us at{' '}
          <a href="mailto:stefano.casafrancalaos@austincc.edu">stefano.casafrancalaos@austincc.edu</a>
        </Typography>
      </Box>
    </Box>
  );
};

const RiverHacks = () => {  
  return (
    <Box sx={{ 
      bgcolor: riverhackColors.spaceBlack,
      color: riverhackColors.cosmicWhite,
      minHeight: '100vh'
    }}>
      {/* Navbar */}
      <RiverHacksNavbar />

      {/* Content sections */}
      <Box>
        {/* Home/Hero Section */}
        <Section id="home" title="RiverHacks &lt;&gt; NASA Space Apps Challenge 2024" fullWidth>
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              height: '100vh',
              backgroundImage: `linear-gradient(135deg, ${riverhackColors.spaceBlack} 0%, ${riverhackColors.starField} 50%, ${riverhackColors.deepPurple}20 100%), url(${nasaInspiration})`,
              backgroundSize: 'cover',
              backgroundPosition: { 
                xs: 'center right', 
                sm: 'center right', 
                md: 'center',
                lg: 'center'
              },
              backgroundRepeat: 'no-repeat',
              backgroundBlendMode: 'overlay',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              color: riverhackColors.cosmicWhite,
              m: 0,
              p: 0,
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: `radial-gradient(circle at 20% 80%, ${riverhackColors.deepPurple}40 0%, transparent 50%), 
                             radial-gradient(circle at 80% 20%, ${riverhackColors.nasaBlue}30 0%, transparent 50%),
                             radial-gradient(circle at 40% 40%, ${riverhackColors.vibrantOrange}20 0%, transparent 50%)`,
                zIndex: 1
              },
              '&::after': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundImage: `
                  radial-gradient(2px 2px at 20px 30px, ${riverhackColors.cosmicWhite}80, transparent),
                  radial-gradient(1px 1px at 40px 70px, ${riverhackColors.cosmicWhite}60, transparent),
                  radial-gradient(1px 1px at 90px 40px, ${riverhackColors.cosmicWhite}90, transparent),
                  radial-gradient(2px 2px at 130px 80px, ${riverhackColors.cosmicWhite}70, transparent),
                  radial-gradient(1px 1px at 160px 30px, ${riverhackColors.cosmicWhite}80, transparent)
                `,
                backgroundSize: '200px 100px',
                animation: 'twinkle 4s ease-in-out infinite alternate',
                zIndex: 1
              }
            }}
          >
            <Container 
              maxWidth="lg" 
              sx={{ 
                position: 'relative', 
                zIndex: 2,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                pt: { xs: 8, md: 0 }
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: { xs: 3, sm: 4, md: 5 },
                  mb: { xs: 4, sm: 5, md: 6 },
                  px: { xs: 2, sm: 3 },
                  flexWrap: { xs: 'wrap', md: 'nowrap' }
                }}
              >
                <Box
                  component="img"
                  src={nasaSpaceAppsLogo}
                  alt="NASA Space Apps Challenge"
                  sx={{
                    maxWidth: { xs: '220px', sm: '280px', md: '320px', lg: '380px' },
                    width: 'auto',
                    height: 'auto'
                  }}
                />
                
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <Typography
                    variant="h3"
                    sx={{
                      color: riverhackColors.cosmicWhite,
                      fontWeight: 'bold',
                      fontSize: { xs: '2.5rem', sm: '3rem', md: '3.5rem' },
                      mx: { xs: 1, sm: 2 },
                      fontFamily: 'monospace'
                    }}
                  >
                    &lt;&gt;
                  </Typography>
                </Box>
                
                <Box
                  component="img"
                  src={riverhacksLogoWhiteStacked}
                  alt="RiverHacks 2025"
                  sx={{
                    maxWidth: { xs: '220px', sm: '280px', md: '320px', lg: '380px' },
                    width: 'auto',
                    height: 'auto'
                  }}
                />
              </Box>
              
              <Box 
                sx={{
                  bgcolor: 'rgba(0, 0, 0, 0.85)',
                  borderRadius: { xs: 2, md: 3 },
                  p: { xs: 3, sm: 4, md: 5 },
                  mx: { xs: 2, sm: 3, md: 'auto' },
                  maxWidth: { xs: 'none', sm: '600px', md: '800px', lg: '900px' },
                  position: 'relative',
                  zIndex: 2,
                  border: `2px solid ${riverhackColors.terracotta}40`,
                  backdropFilter: 'blur(8px)',
                  WebkitBackdropFilter: 'blur(8px)'
                }}
              >
                <Typography 
                  variant="body1" 
                  align="center" 
                  paragraph
                  sx={{ 
                    color: riverhackColors.cosmicWhite,
                    fontSize: { 
                      xs: '1rem',
                      sm: '1.2rem',
                      md: '1.4rem',
                      lg: '1.6rem'
                    },
                    fontWeight: '500',
                    lineHeight: { xs: 1.5, sm: 1.6 },
                    mb: { xs: 2, sm: 3 }
                  }}
                >
                  Join hackers worldwide for NASA's premier space and climate challenge. Build solutions that address real-world problems using space agency data, win amazing prizes, and contribute to space exploration innovation.
                </Typography>
                
                <Typography 
                  variant="h6" 
                  align="center" 
                  sx={{ 
                    color: riverhackColors.terracotta,
                    fontSize: { 
                      xs: '1.2rem',
                      sm: '1.4rem',
                      md: '1.6rem',
                      lg: '1.8rem'
                    },
                    fontWeight: 'bold'
                  }}
                >
                  October 4-5, 2025 | Austin, Texas
                </Typography>
              </Box>
              
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                mt: { xs: 3, sm: 4 },
                px: { xs: 2, sm: 0 }
              }}>
                <Button 
                  component="a"
                  href="https://lu.ma/wudwutav"
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="contained" 
                  color="primary"
                  className="luma-checkout--button"
                  data-luma-action="checkout"
                  data-luma-event-id="wudwutav"
                  size="large"
                  sx={{ 
                    borderRadius: '28px',
                    px: { xs: 3, sm: 4 },
                    py: { xs: 1.2, sm: 1.5 },
                    fontWeight: 'bold',
                    fontSize: { xs: '1rem', sm: '1.1rem' },
                    textDecoration: 'none',
                    bgcolor: riverhackColors.vibrantOrange,
                    minWidth: { xs: '140px', sm: '160px' },
                    '&:hover': {
                      bgcolor: riverhackColors.terracotta,
                      transform: 'translateY(-2px)',
                    },
                    transition: 'all 0.3s ease'
                  }}
                >
                  Register Now
                </Button>
              </Box>
            </Container>
          </Box>
        </Section>

        {/* Main Challenge Banner */}
        <Box
          sx={{
            py: 8,
            px: 3,
            mb: 6,
            background: `linear-gradient(135deg, ${riverhackColors.nasaBlue}40 0%, ${riverhackColors.terracotta}35 100%)`,
            borderTop: `5px solid ${riverhackColors.terracotta}`,
            borderBottom: `5px solid ${riverhackColors.nasaBlue}`,
            boxShadow: `0 20px 60px ${riverhackColors.deepPurple}40`,
            textAlign: 'center'
          }}
        >
          <Typography
            variant="h2"
            sx={{
              color: riverhackColors.cosmicWhite,
              fontWeight: 'bold',
              mb: 2,
              fontSize: { xs: '2rem', sm: '3rem', md: '4rem' },
              textShadow: `2px 2px 8px ${riverhackColors.deepPurple}80`
            }}
          >
            One Main Challenge:
          </Typography>
          <Typography
            variant="h3"
            sx={{
              color: riverhackColors.terracotta,
              fontWeight: 'bold',
              fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3.5rem' },
              textShadow: `2px 2px 8px ${riverhackColors.deepPurple}80`
            }}
          >
            🚀 Build Something Great!
          </Typography>
          <Typography
            variant="h4"
            sx={{
              color: riverhackColors.cosmicWhite,
              fontWeight: '500',
              mt: 2,
              fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
              fontStyle: 'italic'
            }}
          >
            Sky is the limit
          </Typography>
        </Box>

        {/* Main Theme Section */}
        <Section id="main-theme" title="🌌 RiverHacks Inspiration Paths">
          <Box sx={{ maxWidth: '1000px', mx: 'auto' }}>
            <Box sx={{ mb: 6, textAlign: 'center' }}>
              <Typography
                variant="body1"
                sx={{
                  fontSize: '1.2rem',
                  lineHeight: 1.7,
                  color: riverhackColors.cosmicWhite,
                  fontWeight: '400',
                  mb: 2
                }}
              >
                Six original, locally-developed inspiration paths for exploring life in space from all angles — wellness, design, coordination, ai and more.
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: '1.1rem',
                  lineHeight: 1.7,
                  color: riverhackColors.cosmicWhite,
                  fontWeight: '400',
                  fontStyle: 'italic',
                  mb: 6
                }}
              >
                These tracks do not require NASA datasets, making them perfect for creative, interdisciplinary, or beginner-friendly teams.
              </Typography>
            </Box>

            <Grid container spacing={4}>
              <Grid item xs={12}>
                <Grid container spacing={3}>
                  {[
                    {
                      title: '🧠 Mind of the Astronaut',
                      description: 'Mental health & team dynamics, nutrition, rest & daily routines'
                    },
                    {
                      title: '🏠 On-Ship Ecosystem',
                      description: 'Software to monitor, optimize, or simulate water, waste, and food systems in space'
                    },
                    {
                      title: '🤖 AI for Space',
                      description: 'AI-powered solutions to coordinate missions or unlock new space exploration capabilities'
                    },
                    {
                      title: '🏃‍♂️ Space Play & Fitness',
                      description: 'Explore new ways to maintain physical health, movement, and recreation beyond Earth'
                    },
                    {
                      title: '🔍 AI Space Search Engine',
                      description: 'ISS-friendly public data search'
                    },
                    {
                      title: '🚀 Spacesuit AR',
                      description: 'Navigation & daily routine support for lunar missions'
                    }
                  ].map((area, index) => (
                    <Grid item xs={12} md={6} key={index}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 3,
                          height: '100%',
                          bgcolor: riverhackColors.starField,
                          border: `1px solid ${riverhackColors.cosmicWhite}20`,
                          borderLeft: `4px solid ${riverhackColors.terracotta}`
                        }}
                      >
                        <Typography
                          variant="h6"
                          sx={{
                            color: riverhackColors.cosmicWhite,
                            fontWeight: 'bold',
                            mb: 1
                          }}
                        >
                          {area.title}
                        </Typography>
                        <Typography 
                          variant="body1"
                          sx={{
                            color: riverhackColors.cosmicWhite,
                            fontSize: '1rem',
                            lineHeight: 1.6,
                            fontWeight: '400'
                          }}
                        >
                          {area.description}
                        </Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Grid>

              {/* NASA Space Apps Challenges 2025 */}
              <Grid item xs={12} id="nasa-challenges">
                <Box sx={{ mb: 6, mt: 8 }}>
                  {/* NASA Logo */}
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      mb: 4
                    }}
                  >
                    <Box
                      component="img"
                      src={nasaSpaceAppsLogo}
                      alt="NASA Space Apps Challenge"
                      sx={{
                        maxWidth: { xs: '200px', sm: '250px', md: '300px' },
                        width: 'auto',
                        height: 'auto'
                      }}
                    />
                  </Box>

                  <Typography
                    variant="h4"
                    sx={{
                      color: riverhackColors.nasaBlue,
                      fontWeight: 'bold',
                      mb: 3,
                      textAlign: 'center',
                      fontSize: { xs: '1.75rem', md: '2.5rem' }
                    }}
                  >
                    NASA Space Apps Challenges 2025
                  </Typography>

                  <Typography
                    variant="h5"
                    sx={{
                      color: riverhackColors.nasaBlue,
                      fontWeight: 'bold',
                      mb: 3,
                      textAlign: 'center'
                    }}
                  >
                    🚀 Full List in Order
                  </Typography>

                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: '1.1rem',
                      lineHeight: 1.7,
                      color: riverhackColors.cosmicWhite,
                      fontWeight: '400',
                      mb: 4,
                      textAlign: 'center'
                    }}
                  >
                    Preview of the first 6 official NASA Space Apps Challenge categories. Each challenge is designed to address real-world problems using NASA's open data and resources.
                  </Typography>

                  <Box sx={{ position: 'relative', mb: 6 }}>
                    <Grid container spacing={2}>
                      {[
                        "Animation Celebration of Terra Data! → Animate data from Terra's five instruments to showcase an Earth science story.",
                        "Meteor Madness → Develop an interactive simulation tool using real asteroid data to model impact scenarios and mitigation strategies.",
                        "Embiggen Your Eyes! → Build a platform to explore huge NASA image datasets, label known features, and discover new patterns.",
                        "Commercializing Low Earth Orbit (LEO) → Explore opportunities and challenges in the growing commercialization of low Earth orbit.",
                        "BloomWatch: An Earth Observation Application for Global Flowering Phenology → Create a tool that uses NASA data to track and visualize blooming events worldwide.",
                        "International Space Station 25th Anniversary Apps → Design an app to celebrate the ISS anniversary, highlighting astronaut experiences and cupola views."
                      ].map((challenge, index) => (
                        <Grid item xs={12} md={6} key={index}>
                          <Paper
                            elevation={0}
                            sx={{
                              p: 2,
                              bgcolor: riverhackColors.starField,
                              border: `1px solid ${riverhackColors.cosmicWhite}20`,
                              borderLeft: `4px solid ${riverhackColors.nasaBlue}`
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                color: riverhackColors.cosmicWhite,
                                fontSize: '0.9rem',
                                lineHeight: 1.5,
                                fontWeight: '400'
                              }}
                            >
                              <Box component="span" sx={{ color: riverhackColors.terracotta, fontWeight: 'bold' }}>
                                {index + 1}.
                              </Box>{' '}
                              {challenge}
                            </Typography>
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>

                    {/* Gradient Overlay */}
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: '120px',
                        background: `linear-gradient(transparent, ${riverhackColors.spaceBlack})`,
                        pointerEvents: 'none'
                      }}
                    />
                  </Box>

                  <Box sx={{ textAlign: 'center' }}>
                    <Button
                      component="a"
                      href="https://www.spaceappschallenge.org/2025/challenges/"
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="contained"
                      size="large"
                      sx={{
                        borderRadius: '28px',
                        px: 6,
                        py: 2,
                        fontSize: '1.2rem',
                        fontWeight: 'bold',
                        textDecoration: 'none',
                        bgcolor: riverhackColors.nasaBlue,
                        color: riverhackColors.cosmicWhite,
                        boxShadow: `0 4px 20px ${riverhackColors.nasaBlue}40`,
                        '&:hover': {
                          bgcolor: '#0077DD',
                          transform: 'translateY(-2px)',
                          boxShadow: `0 6px 25px ${riverhackColors.nasaBlue}60`,
                        },
                        transition: 'all 0.3s ease'
                      }}
                    >
                      🌌 See All NASA Challenges
                    </Button>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Typography
                  variant="h4"
                  sx={{
                    color: riverhackColors.terracotta,
                    fontWeight: 'bold',
                    mb: 4,
                    textAlign: 'center'
                  }}
                >
                  🏆 RiverHacks Awards
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: '1.2rem',
                    lineHeight: 1.7,
                    color: riverhackColors.cosmicWhite,
                    fontWeight: '400',
                    mb: 4
                  }}
                >
                  Get inspired by our six Inspiration Paths – or blaze your own trail!
                  <br />
                  This year, we're keeping it simple: one main challenge, one unified rubric, multiple ways to win.
                </Typography>
                
                {/* Prize Money Section */}
                <Typography
                  variant="h6"
                  sx={{
                    color: riverhackColors.terracotta,
                    fontWeight: 'bold',
                    mb: 3
                  }}
                >
                  💰 RiverHacks Top 3 Overall:
                </Typography>
                <Grid container spacing={3} sx={{ mb: 4 }}>
                  {[
                    { place: '🥇 1st Place', prize: '$3,000' },
                    { place: '🥈 2nd Place', prize: '$1,500' },
                    { place: '🥉 3rd Place', prize: '$500' }
                  ].map((award, index) => (
                    <Grid item xs={12} md={4} key={index}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 3,
                          textAlign: 'center',
                          bgcolor: riverhackColors.starField,
                          border: `1px solid ${riverhackColors.cosmicWhite}20`,
                          borderTop: `4px solid ${riverhackColors.terracotta}`
                        }}
                      >
                        <Typography
                          variant="h6"
                          sx={{
                            color: riverhackColors.cosmicWhite,
                            fontWeight: 'bold',
                            mb: 1
                          }}
                        >
                          {award.place}
                        </Typography>
                        <Typography
                          variant="h5"
                          sx={{
                            color: riverhackColors.terracotta,
                            fontWeight: 'bold'
                          }}
                        >
                          {award.prize}
                        </Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>

                {/* NASA Top 3 Recognition */}
                <Box
                  sx={{
                    mb: 6,
                    p: 3,
                    bgcolor: riverhackColors.starField,
                    border: `2px solid ${riverhackColors.nasaBlue}40`,
                    borderRadius: 2
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: '1.1rem',
                      lineHeight: 1.7,
                      color: riverhackColors.cosmicWhite,
                      fontWeight: '400',
                      textAlign: 'center'
                    }}
                  >
                    <strong style={{ color: riverhackColors.nasaBlue }}>NASA Top 3 Overall</strong> get the chance to advance to global finals and worldwide recognition by NASA
                  </Typography>
                </Box>

                {/* Bonus Opportunities - Enhanced */}
                <Box
                  sx={{
                    mt: 6,
                    p: 4,
                    background: `linear-gradient(135deg, ${riverhackColors.terracotta}25 0%, ${riverhackColors.nasaBlue}20 100%)`,
                    borderRadius: 3,
                    border: `3px solid ${riverhackColors.terracotta}60`,
                    boxShadow: `0 12px 40px ${riverhackColors.terracotta}30`,
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                >
                  <Typography
                    variant="h4"
                    sx={{
                      color: riverhackColors.terracotta,
                      fontWeight: 'bold',
                      mb: 3,
                      textAlign: 'center',
                      fontSize: { xs: '1.75rem', md: '2.5rem' }
                    }}
                  >
                    🎁 Bonus Opportunities!
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      fontSize: { xs: '1.1rem', md: '1.5rem' },
                      lineHeight: 1.8,
                      color: riverhackColors.cosmicWhite,
                      fontWeight: '500',
                      textAlign: 'center'
                    }}
                  >
                    Teams with their same project, if it uses search engines and AI, can be considered for the <strong style={{ color: riverhackColors.terracotta, fontSize: '1.2em' }}>Bold Innovation in Search & AI Award</strong> (sponsor-selected, up to <strong style={{ color: riverhackColors.terracotta, fontSize: '1.3em' }}>$5,000</strong>).
                  </Typography>
                </Box>

                {/* Judging Criteria */}
                <Typography
                  variant="h6"
                  sx={{
                    color: riverhackColors.terracotta,
                    fontWeight: 'bold',
                    mb: 3,
                    mt: 6
                  }}
                >
                  📊 Judging Criteria:
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: '1.1rem',
                    lineHeight: 1.7,
                    color: riverhackColors.cosmicWhite,
                    fontWeight: '400',
                    mb: 3
                  }}
                >
                  Projects will be evaluated on:
                </Typography>
                <Grid container spacing={2} sx={{ mb: 4 }}>
                  {[
                    'Technical Depth & Innovation – Complexity, originality, completeness',
                    'Value & Impact – Problem understanding, relevance, feasibility',
                    'Build Quality & UX – Reliability, clarity, accessibility, documentation',
                    'Presentation & Communication – Storytelling, explanation, live demo'
                  ].map((criteria, index) => (
                    <Grid item xs={12} md={6} key={index}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2,
                          bgcolor: riverhackColors.starField,
                          border: `1px solid ${riverhackColors.cosmicWhite}20`,
                          borderLeft: `4px solid ${riverhackColors.terracotta}`
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            color: riverhackColors.cosmicWhite,
                            fontSize: '1rem',
                            lineHeight: 1.5,
                            fontWeight: '400'
                          }}
                        >
                          • {criteria}
                        </Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            </Grid>
          </Box>
        </Section>

        {/* Announcements Section */}
        <Section id="announcements" title="Announcements">
          <Stack spacing={3} sx={{ maxWidth: '800px', mx: 'auto' }}>
            {announcements.length > 0 ? (
              announcements.map((announcement) => (
                <Paper
                  key={announcement.id}
                  elevation={0}
                  sx={{
                    position: 'relative',
                    p: 4,
                    bgcolor: riverhackColors.sandBeige,
                    border: `8px solid ${riverhackColors.deepPurple}`,
                    boxShadow: `15px 15px 0 ${riverhackColors.deepPurple}`,
                    transform: 'rotate(-2deg)',
                    transition: 'all 0.3s ease',
                    overflow: 'hidden',
                    '&:hover': {
                      transform: 'rotate(0deg) scale(1.02)',
                      boxShadow: `20px 20px 0 ${riverhackColors.deepPurple}`,
                    }
                  }}
                >
                  {/* Corner Ribbon */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -50,
                      right: -50,
                      height: '100px',
                      width: '100px',
                      bgcolor: riverhackColors.vibrantOrange,
                      transform: 'rotate(45deg)',
                    }}
                  >
                    <Typography
                      sx={{
                        position: 'absolute',
                        bottom: '-5px',
                        left: '0',
                        width: '100%',
                        color: 'white',
                        fontWeight: 'bold',
                        textTransform: 'uppercase',
                        fontSize: '14px',
                        letterSpacing: '1px',
                        textAlign: 'center',
                      }}
                    >
                      New Update
                    </Typography>
                  </Box>

                  <Typography 
                    component="span" 
                    sx={{ 
                      display: 'block',
                      fontSize: '24px',
                      fontWeight: 700,
                      color: riverhackColors.deepPurple,
                      textTransform: 'uppercase',
                      mb: 1,
                      borderBottom: `2px solid ${riverhackColors.deepPurple}`,
                      width: '50%'
                    }}
                  >
                    {announcement.published.toLocaleDateString('en-us', {
                      month: 'long',
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </Typography>

                  <Typography 
                    component="div" 
                    sx={{ 
                      color: riverhackColors.spaceBlack,
                      fontSize: '16px',
                      lineHeight: 1.4,
                      mb: 2,
                      fontWeight: '500',
                      '& p': { mt: 1 },
                      '& strong': { 
                        color: riverhackColors.vibrantOrange,
                        fontWeight: 'bold'
                      }
                    }}
                  >
                    <ReactMarkdown>{announcement.body}</ReactMarkdown>
                  </Typography>
                </Paper>
              ))
            ) : (
              <Typography 
                align="center" 
                sx={{ 
                  color: riverhackColors.cosmicWhite,
                  fontSize: '1.2rem',
                  my: 4 
                }}
              >
                There are no announcements at this time.
              </Typography>
            )}
          </Stack>
        </Section>

        {/* Schedule Section */}
        <Section id="schedule" title="Schedule">
          <ScheduleSection />
        </Section>

        {/* FAQ Section */}
        <Section id="faq" title="FAQ">
          <FaqSection />
        </Section>


        {/* Collaborators Section */}
        <Section id="collaborators" title="Collaborators">
          <Box sx={{ maxWidth: '1000px', mx: 'auto', textAlign: 'center' }}>
            <Typography
              variant="body1"
              sx={{
                mb: 4,
                color: riverhackColors.cosmicWhite,
                fontSize: '1.1rem',
                lineHeight: 1.6,
                fontWeight: '400'
              }}
            >
              Special thanks to our collaborators who help make RiverHacks 2025 possible through their support and partnership.
            </Typography>
            
            {/* SWIFT - Large Featured Collaborator */}
            <Box sx={{ mb: 4, display: 'flex', justifyContent: 'center' }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 4,
                  bgcolor: 'background.paper',
                  borderRadius: 2,
                  border: `1px solid ${riverhackColors.deepPurple}20`,
                  height: '250px',
                  width: { xs: '100%', sm: '540px', md: '720px' },
                  maxWidth: '100%'
                }}
              >
                <Box
                  component="img"
                  src={swiftLogo}
                  alt="SWIFT logo"
                  sx={{
                    width: '120%',
                    height: '120%',
                    objectFit: 'contain'
                  }}
                />
              </Box>
            </Box>

            <Grid container spacing={4} justifyContent="center">
              {/* ACM at ACC */}
              <Grid item xs={12} sm={6} md={4}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 3,
                    bgcolor: 'background.paper',
                    borderRadius: 2,
                    border: `1px solid ${riverhackColors.deepPurple}20`,
                    height: '150px'
                  }}
                >
                  <Box
                    component="img"
                    src={acmAccLogo}
                    alt="ACM at ACC logo"
                    sx={{
                      width: '120%',
                      height: '120%',
                      objectFit: 'contain'
                    }}
                  />
                </Box>
              </Grid>

              {/* Loop Message */}
              <Grid item xs={12} sm={6} md={4}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 3,
                    bgcolor: 'background.paper',
                    borderRadius: 2,
                    border: `1px solid ${riverhackColors.deepPurple}20`,
                    height: '150px'
                  }}
                >
                  <Box
                    component="img"
                    src={loopMessageLogo}
                    alt="Loop Message logo"
                    sx={{
                      width: '120%',
                      height: '120%',
                      objectFit: 'contain'
                    }}
                  />
                </Box>
              </Grid>

              {/* Girlstart */}
              <Grid item xs={12} sm={6} md={4}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 3,
                    bgcolor: 'background.paper',
                    borderRadius: 2,
                    border: `1px solid ${riverhackColors.deepPurple}20`,
                    height: '150px'
                  }}
                >
                  <Box
                    component="img"
                    src={girlstartLogo}
                    alt="Girlstart logo"
                    sx={{
                      width: '120%',
                      height: '120%',
                      objectFit: 'contain'
                    }}
                  />
                </Box>
              </Grid>

              {/* Fibonacci Food */}
              <Grid item xs={12} sm={6} md={4}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 3,
                    bgcolor: 'background.paper',
                    borderRadius: 2,
                    border: `1px solid ${riverhackColors.deepPurple}20`,
                    height: '150px'
                  }}
                >
                  <Box
                    component="img"
                    src={fibonacciFoodLogo}
                    alt="Fibonacci Food logo"
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain'
                    }}
                  />
                </Box>
              </Grid>

              {/* Bespoke Food */}
              <Grid item xs={12} sm={6} md={4}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 3,
                    bgcolor: 'background.paper',
                    borderRadius: 2,
                    border: `1px solid ${riverhackColors.deepPurple}20`,
                    height: '150px'
                  }}
                >
                  <Box
                    component="img"
                    src={bespokeFoodLogo}
                    alt="Bespoke Food logo"
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain'
                    }}
                  />
                </Box>
              </Grid>

            </Grid>
          </Box>
        </Section>


        {/* Sponsors Section */}
        <Section id="sponsors" title="Sponsors">
          <SponsorsSection />
        </Section>

        {/* Special Thanks Section */}
        <Section id="special-thanks" title="">
          <Box sx={{ maxWidth: '800px', mx: 'auto', textAlign: 'center' }}>
            <Typography
              variant="h4"
              sx={{
                color: riverhackColors.terracotta,
                fontWeight: 'bold',
                mb: 4
              }}
            >
              🙏 Special Thanks
            </Typography>
            
            <Box sx={{ mb: 6 }}>
              <Box
                sx={{
                  background: `linear-gradient(135deg, ${riverhackColors.terracotta}20 0%, ${riverhackColors.sandBeige}15 100%)`,
                  borderRadius: 4,
                  p: 8,
                  border: `3px solid ${riverhackColors.terracotta}40`,
                  boxShadow: `0 12px 40px ${riverhackColors.deepPurple}30`,
                  maxWidth: '700px',
                  mx: 'auto'
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 4
                  }}
                >
                  <Box
                    component="img"
                    src={cgcsLogo}
                    alt="CGCS logo"
                    sx={{
                      width: '500px',
                      height: '250px',
                      objectFit: 'contain',
                      filter: 'brightness(1.1)'
                    }}
                  />
                  <Typography
                    variant="h5"
                    sx={{
                      color: riverhackColors.cosmicWhite,
                      textAlign: 'center',
                      opacity: 0.9,
                      fontStyle: 'italic',
                      fontSize: '1.3rem',
                      lineHeight: 1.6
                    }}
                  >
                    Thank you CGCS for being our venue partner and supporting student innovation at RiverHacks 2025
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Section>

        {/* Contact Section */}
        <Section id="contact" title="Contact Us">
          <Box sx={{ 
            maxWidth: '600px', 
            mx: 'auto', 
            textAlign: 'center',
            px: { xs: 2, sm: 3, md: 0 }
          }}>
            <Typography
              variant="h5"
              sx={{
                color: riverhackColors.cosmicWhite,
                mb: { xs: 3, sm: 4 },
                fontWeight: 'medium',
                fontSize: { xs: '1.3rem', sm: '1.5rem', md: '1.8rem' }
              }}
            >
              Have questions or want to become a sponsor?
            </Typography>
            
            <Typography
              variant="body1"
              sx={{
                color: riverhackColors.cosmicWhite,
                mb: 4,
                fontSize: '1.2rem',
                lineHeight: 1.6
              }}
            >
              Reach out to us for sponsorship opportunities, general inquiries, or any questions about RiverHacks 2025.
            </Typography>

            <Paper
              elevation={0}
              sx={{
                p: 4,
                bgcolor: riverhackColors.starField,
                border: `1px solid ${riverhackColors.cosmicWhite}20`,
                borderRadius: 2,
                mb: 4
              }}
            >
              <Stack spacing={{ xs: 2, sm: 3 }} alignItems="center">
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: { xs: 1.5, sm: 2 },
                  flexWrap: 'wrap',
                  justifyContent: 'center'
                }}>
                  <EmailIcon sx={{ 
                    color: riverhackColors.terracotta, 
                    fontSize: { xs: '1.5rem', sm: '1.8rem', md: '2rem' }
                  }} />
                  <Typography
                    variant="h6"
                    sx={{
                      color: riverhackColors.cosmicWhite,
                      fontWeight: 'medium',
                      fontSize: { xs: '1rem', sm: '1.1rem', md: '1.3rem' },
                      wordBreak: 'break-all',
                      textAlign: 'center'
                    }}
                  >
                    stefano.casafrancalaos@austincc.edu
                  </Typography>
                </Box>
                
                <Button
                  variant="contained"
                  onClick={() => {
                    navigator.clipboard.writeText('stefano.casafrancalaos@austincc.edu');
                    // You could add a toast notification here if needed
                  }}
                  startIcon={<ContentCopyIcon />}
                  sx={{
                    bgcolor: riverhackColors.vibrantOrange,
                    borderRadius: '28px',
                    px: { xs: 2, sm: 3 },
                    py: { xs: 1, sm: 1.2 },
                    fontWeight: 'bold',
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                    '&:hover': {
                      bgcolor: riverhackColors.terracotta,
                    }
                  }}
                >
                  Copy Email Address
                </Button>
              </Stack>
            </Paper>
          </Box>
        </Section>
      </Box>

      {/* Add ScrollToTop button */}
      <ScrollToTop />
    </Box>
  );
};

export default RiverHacks;
